import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { NavLink } from "react-router-dom";
import styled from "styled-components";

export const InnerContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 56px;
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
`;

export const Submenu = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 0 0 140px;

  & a {
    color: #181818;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    cursor: pointer;
    padding: 8px;
    border-radius: 4px;
    text-decoration: none;

    &:hover {
      background-color: #ebf4ff;
    }
  }
`;

export const EntityContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 0%;
  gap: 56px;
  min-width: 0;
  max-width: 100%;
`;

export const EntityScroller = styled.div.attrs({
  "data-testid": "content-scroll",
} as Record<string, string>)`
  min-height: 0;
  min-width: 0;
  flex: 1 1 auto;
  overflow: auto;
  padding-right: 16px;
`;

export const Entity = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const EntityTitle = styled.h2`
  color: #181818;
  font-size: 18px;
  font-weight: 600;
  line-height: 26px;
`;

export const TableWrapper = styled.div`
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: visible;

  ${(props) => {
    if (props.theme.mode === "dark") {
      return `
          .p-paginator {
            background: #f8f8f8;
            
            & * {
              color: black;
            }
          }
      `;
    }
  }}
`;

export const TableActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  flex-wrap: nowrap;
  padding: 16px;
  border-bottom: 1px solid
    ${({ theme }) => theme.searchPatients.controls.tabOutline};
  background: #ffffff;

  @media (max-width: 1080px) {
    align-items: flex-start;
    flex-wrap: wrap;
  }
`;

export const TableActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: nowrap;
  min-width: 0;

  &:last-child {
    margin-left: auto;
  }

  @media (max-width: 1080px) {
    flex-wrap: wrap;

    &:last-child {
      margin-left: 0;
    }
  }
`;

export const DownloadControls = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #181818;
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
`;

export const DownloadOptions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 3px;
`;

export const DownloadLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  color: #181818;
  text-decoration: none;
  cursor: pointer;

  & svg {
    display: block;
  }
`;

export const Table = styled(DataTable)`
  width: 100%;
  max-width: 100%;

  &.p-datatable .p-datatable-wrapper.p-datatable-wrapper {
    width: 100%;
    overflow: visible;
    overflow-x: visible;
    overflow-y: visible;
    max-width: 100%;
  }

  & .p-datatable-table {
    width: auto;
    min-width: max-content;
  }

  & .p-datatable-scrollable-table {
    width: auto;
    min-width: max-content;
  }

  &.is-flowsheets-table .p-datatable-table,
  &.is-flowsheets-table .p-datatable-scrollable-table {
    width: 100%;
    min-width: 100%;
    table-layout: fixed;
  }

  & tbody tr.is-clickable-row {
    cursor: pointer;
  }

  && .p-datatable-tbody > tr:hover > td {
    background: #f8f8f8;
  }

  & tbody tr.is-clickable-row:hover td {
    background: #f8f8f8;
  }

  & tbody tr.is-clickable-row:hover .row-action-icon {
    color: #065fa6;
  }

  && .p-datatable-thead > tr > th {
    position: sticky;
    top: 0;
    z-index: 6;
    padding: 12px 10px;
    background: white;
    color: #181818;
    border: 1px solid ${({ theme }) => theme.searchPatients.controls.tabOutline};
    border-width: 0 0 1px 0;
    white-space: nowrap;

    & .p-sortable-column-icon {
      margin-left: 10px;
      color: #7c7f7e;
      transition: color 0.2s ease;
    }

    & .table-sort-icon {
      display: inline-flex;
      align-items: center;
      margin-left: 10px;
      vertical-align: middle;
    }

    &.p-highlight .p-sortable-column-icon {
      color: #065fa6;
    }
  }

  && .p-datatable-tbody > tr > td {
    padding: 18px 10px;
    background: white;
    color: #181818;
    border: 1px solid ${({ theme }) => theme.searchPatients.controls.tabOutline};
    border-width: 0 0 1px 0;
    white-space: nowrap;
  }

  && .p-frozen-column {
    background: #ffffff;
  }

  & thead tr th.p-frozen-column {
    z-index: 7;
  }

  & tbody tr td.p-frozen-column {
    z-index: 4;
  }

  & tbody tr.is-clickable-row:hover td.p-frozen-column {
    background: #f8f8f8;
  }

  & .is-roi-status-column {
    border-left: none;
    border-right: none;
    box-shadow: 0px 4px 10px 0px rgba(10, 45, 73, 0.16);
    padding-left: 20px;
  }

  & .is-roi-actions-column {
    border-left: none;
    border-right: none;
    text-align: center;
    padding-right: 30px;
  }

  & .is-table-spacer-column {
    padding-left: 0;
    padding-right: 0;
  }
`;

export const NoData = styled.div`
  width: 100%;
  height: 84px;
  background-color: #f8f8f8;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  color: #5a5959;
`;

export const EmptyState = styled.div`
  width: 100%;
  // temp height untill layout component is implemented, should be able to adjust based on content
  height: 406px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 22px;
  color: #5a5959;
`;

export const EmptyStateIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  & svg {
    display: block;
  }
`;

export const EmptyStateMessage = styled.div`
  color: #5a5959;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0;
  text-align: center;
`;

export const EmptyStatePanel = styled.div`
  width: 100%;
  height: 84px;
  border-radius: 8px;
  background-color: #f8f8f8;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #5a5959;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
`;

export const PaginatorBtn = styled.button`
  background: none;
  border: none;
  padding: 15px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.gray};
  margin: 5px;

  &:disabled {
    color: #9fa0a4;
  }

  &:hover {
    color: #181818;
    background-color: #ebf4ff;
    border-radius: 4px;
  }
`;

export const PaginatorPageNumber = styled.span`
  color: #181818;
  font-size: 14px;
  line-height: 20px;
  min-width: 16px;
  text-align: center;
  user-select: none;
`;

export const PageReportTitle = styled.span`
  color: #181818;
  user-select: none;
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
`;

export const RowsPerPageWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 36px;
  color: #181818;
`;

export const RowsPerPageLabel = styled.span`
  color: #5a5959;
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
  user-select: none;
`;

export const RowsPerPage = styled(Dropdown)`
  min-width: 76px;

  &.p-dropdown {
    border: 1px solid ${({ theme }) => theme.searchPatients.controls.tabOutline};
    border-radius: 8px;
    box-shadow: none;
    background: #ffffff;
    height: 38px;
  }

  & .p-dropdown-label {
    padding: 8px 8px 8px 12px;
    color: #181818;
    font-size: 14px;
    line-height: 20px;
  }

  & .p-dropdown-trigger {
    width: 32px;
    color: #5a5959;
  }

  &:not(.p-disabled).p-focus,
  &:not(.p-disabled):hover {
    border-color: #065fa6;
    box-shadow: 0 0 0 1px rgba(6, 95, 166, 0.12);
  }
`;

export const ViewButton = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #005fb8;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  & i {
    font-size: 16px;
  }
`;

export const PaginatorWrapper = styled.div`
  position: relative;
  width: auto;
  min-height: 46px;
  flex: 0 0 auto;

  .p-paginator {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 16px;
    padding: 0;
  }

  .p-paginator-first,
  .p-paginator-last {
    display: none;
  }

  .p-paginator-prev {
    margin-left: 8px;
  }

  .p-paginator .p-dropdown-panel {
    border-radius: 8px;
  }

  .p-paginator-pages {
    button {
      font-size: 14px;
      position: relative;
      top: -2px;
    }
  }

  .p-paginator > button {
    padding: 4px;
  }

  @media (max-width: 768px) {
    width: 100%;
    flex: 1 1 100%;
    overflow-x: auto;
  }
`;

export const TableSearch = styled.form`
  position: relative;
  display: flex;
  align-items: center;
  width: 360px;
  min-width: 280px;

  @media (max-width: 768px) {
    width: 100%;
    min-width: 0;
  }
`;

export const TableSearchInput = styled.input`
  width: 100%;
  height: 46px;
  padding: 0 48px 0 16px;
  border: 1px solid ${({ theme }) => theme.searchPatients.controls.tabOutline};
  border-radius: 8px;
  background: #ffffff;
  color: #181818;
  font-size: 14px;
  line-height: 20px;

  &::placeholder {
    color: #7c7f7e;
  }

  &:focus {
    outline: none;
  }
`;

export const TableSearchIcon = styled.button`
  position: absolute;
  top: 50%;
  right: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: #7c7f7e;
  cursor: pointer;
  padding: 0;
  font-size: 16px;
`;

export const CellContent = styled.div<{
  $align?: "left" | "center" | "right";
}>`
  display: flex;
  align-items: center;
  justify-content: ${({ $align = "left" }) => {
    switch ($align) {
      case "center":
        return "center";
      case "right":
        return "flex-end";
      default:
        return "flex-start";
    }
  }};
  width: 100%;
  min-height: 20px;
`;

export const DocLink = styled.a`
  color: #065fa6;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background-color: #ebf4ff;
  }
`;

export const DetailsLink = styled(NavLink)`
  color: #181818;
  text-decoration: underline;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`;

export const RoiStatus = styled.div<{ status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;

  &:before {
    content: "";
    display: inline-block;
    flex: 0 0 auto;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: ${(props) => {
      switch (props.status) {
        case "Completed":
          return "#4CAF50";
        case "Cancelled":
          return "#959595";
        case "Draft":
          return "#E6BD1A";
        default:
          return "#9E9E9E";
      }
    }};
  }
`;

export const RoiControls = styled.div`
  display: flex;
  flex-direction: row;
`;

export const RoiButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  padding: 8px;
  border-radius: 4px;
  color: ${({ theme }) => theme.text.secondary};
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.sidebarHeader.background1};
    color: ${({ theme }) => theme.buttons.style1.bg};
  }

  & i {
    color: inherit;
    font-size: 16px;
  }

  & svg {
    display: block;
  }
`;

export const RoiActionLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;
